import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { CheckCircle2, StickyNote, FileUp, MessageSquare, ArrowRight, Clock, CalendarClock, CalendarCheck, Play, XCircle, UserCog, Smartphone, FileCheck2 } from "lucide-react";

const ICON = {
  status_change: CheckCircle2,
  note_added: StickyNote,
  document_uploaded: FileUp,
  credit_report_uploaded: FileCheck2,
  message_sent: MessageSquare,
  sms_sent: Smartphone,
  schedule_options_sent: CalendarClock,
  schedule_confirmed: CalendarCheck,
  schedule_confirmed_by_client: CalendarCheck,
  schedule_started: Play,
  schedule_completed: CheckCircle2,
  schedule_canceled: XCircle,
  schedule_rescheduled: CalendarClock,
  schedule_reassigned: UserCog,
};

const ACCENT = {
  status_change: "bg-amber-100 text-amber-700",
  note_added: "bg-slate-100 text-slate-700",
  document_uploaded: "bg-sky-100 text-sky-700",
  credit_report_uploaded: "bg-amber-100 text-amber-700",
  message_sent: "bg-emerald-100 text-emerald-700",
  sms_sent: "bg-teal-100 text-teal-700",
  schedule_options_sent: "bg-indigo-100 text-indigo-700",
  schedule_confirmed: "bg-amber-100 text-amber-700",
  schedule_confirmed_by_client: "bg-amber-100 text-amber-700",
  schedule_started: "bg-blue-100 text-blue-700",
  schedule_completed: "bg-emerald-100 text-emerald-700",
  schedule_canceled: "bg-red-100 text-red-700",
  schedule_rescheduled: "bg-indigo-100 text-indigo-700",
  schedule_reassigned: "bg-purple-100 text-purple-700",
};

function fmtTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch { return iso; }
}

function label(ev) {
  switch (ev.type) {
    case "status_change":
      return (
        <span className="text-slate-800">
          Status changed <span className="font-mono text-xs text-slate-500">{ev.meta?.from || "—"}</span>
          <ArrowRight size={12} className="inline mx-1 -mt-0.5" />
          <span className="font-bold text-amber-700">{ev.meta?.to}</span>
        </span>
      );
    case "note_added":
      if (ev.meta?.generic_label) {
        return <span className="text-slate-800">{ev.meta.generic_label}</span>;
      }
      return <span className="text-slate-800">Internal note added <span className="text-slate-500">— "{ev.meta?.preview}"</span></span>;
    case "document_uploaded":
      if (ev.meta?.generic_label) {
        return <span className="text-slate-800">{ev.meta.generic_label}</span>;
      }
      return <span className="text-slate-800">Document uploaded <span className="text-slate-500">— {ev.meta?.filename}</span> <span className="text-xs text-slate-400 ml-1">({ev.meta?.source})</span></span>;
    case "message_sent":
      return <span className="text-slate-800">Message sent <span className="text-slate-500">— "{ev.meta?.preview}"</span></span>;
    case "sms_sent":
      return <span className="text-slate-800">SMS sent via Close to <span className="font-mono text-xs">{ev.meta?.to}</span> <span className="text-slate-500">— "{ev.meta?.preview}"</span></span>;
    case "credit_report_uploaded":
      return <span className="text-slate-800">Credit report analyzed <span className="text-slate-500">— {ev.meta?.source_hint || ev.meta?.bureau || "bureau"} · {ev.meta?.tradeline_count} tradelines · {ev.meta?.score_count} score{ev.meta?.score_count === 1 ? "" : "s"}</span></span>;
    case "schedule_options_sent":
      return <span className="text-slate-800">Processing times sent <span className="text-slate-500">— {ev.meta?.operation_type} · {ev.meta?.option_count} option(s) with {ev.meta?.assignee_name}</span></span>;
    case "schedule_confirmed":
      return <span className="text-slate-800">Scheduled directly <span className="text-slate-500">— {ev.meta?.operation_type} · {fmtTime(ev.meta?.scheduled_start)} · {ev.meta?.assignee_name}</span></span>;
    case "schedule_confirmed_by_client":
      return <span className="text-slate-800">Client selected a time <span className="font-bold text-amber-700">{fmtTime(ev.meta?.scheduled_start)}</span> <span className="text-slate-500">— {ev.meta?.operation_type} with {ev.meta?.assignee_name}</span></span>;
    case "schedule_started":
      return <span className="text-slate-800"><span className="font-bold text-blue-700">{ev.actor_name || "The team"}</span> started processing <span className="text-slate-500">— {ev.meta?.operation_type}</span></span>;
    case "schedule_completed":
      return <span className="text-slate-800">Processing completed <span className="text-slate-500">— {ev.meta?.operation_type}</span></span>;
    case "schedule_canceled":
      return <span className="text-slate-800">Scheduled work canceled <span className="text-slate-500">— {ev.meta?.operation_type}</span></span>;
    case "schedule_rescheduled":
      return <span className="text-slate-800">Rescheduled to <span className="font-bold text-indigo-700">{fmtTime(ev.meta?.scheduled_start)}</span></span>;
    case "schedule_reassigned":
      return <span className="text-slate-800">Reassigned to <span className="font-bold text-purple-700">{ev.meta?.assignee_name}</span></span>;
    default:
      return <span className="text-slate-600">{ev.type}</span>;
  }
}

export default function AuditTimeline({ endpoint, refreshKey }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(endpoint).then(({ data }) => setEvents(data)).catch(() => setEvents([])).finally(() => setLoading(false));
  }, [endpoint, refreshKey]);

  return (
    <Card className="p-6 rounded-2xl border-slate-100" data-testid="audit-timeline">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-9 h-9 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center"><Clock size={18} /></div>
        <div>
          <h3 className="font-display font-bold text-lg">Activity timeline</h3>
          <p className="text-xs text-slate-500">Every important thing that's happened on this file.</p>
        </div>
      </div>
      {loading && <p className="text-sm text-slate-400">Loading…</p>}
      {!loading && events.length === 0 && <p className="text-sm text-slate-400" data-testid="timeline-empty">No activity yet.</p>}
      <ol className="relative space-y-4">
        {events.map((ev) => {
          const Icon = ICON[ev.type] || Clock;
          return (
            <li key={ev.id} className="flex gap-3" data-testid={`event-${ev.type}`}>
              <div className={`shrink-0 w-9 h-9 rounded-full flex items-center justify-center ${ACCENT[ev.type] || "bg-slate-100 text-slate-600"}`}>
                <Icon size={16} />
              </div>
              <div className="flex-1 min-w-0 pt-1">
                <div className="text-sm">{label(ev)}</div>
                <div className="text-xs text-slate-400 mt-1">
                  {ev.actor_name || "System"} · {new Date(ev.created_at).toLocaleString()}
                </div>
              </div>
            </li>
          );
        })}
      </ol>
    </Card>
  );
}
